import { ApiError } from "@/lib/apiClient";
import {
  Page,
  PageState,
  CreatePageRequest,
  PageDraft,
  PageDraftResponse,
} from "@/types/models/page";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

const initialState: PageState = {
  items: [] as Page[],
  loading: false,
  pagesFetched: false,
  error: null,
  pagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  form: {
    loading: false,
    success: false,
    error: null,
  },
  currentPage: null,
  currentPageLoading: false,
  currentPageError: null,
  // Draft-related state
  drafts: [] as PageDraft[],
  draftsLoading: false,
  draftsError: null,
  currentDraft: null,
  currentDraftLoading: false,
  currentDraftError: null,
  draftForm: {
    loading: false,
    success: false,
    error: null,
  },
};

const pageSlice = createSlice({
  name: "page",
  initialState,
  reducers: {
    // -------- LIST PAGES --------
    fetchPagesRequest: (
      state,
      action: PayloadAction<{ page: number; pageSize: number }>,
    ) => {
      state.loading = true;
      state.pagesFetched = false;
      state.error = null;
      state.pagination.page = action.payload.page;
    },
    fetchPagesSuccess: (
      state,
      action: PayloadAction<{
        list: Page[];
        pagination: {
          page: number;
          total: number;
          pageSize: number;
          totalPages: number;
        };
      }>,
    ) => {
      state.items = action.payload.list;
      state.pagination = action.payload.pagination;
      state.loading = false;
      state.pagesFetched = true;
    },
    fetchPagesFailure: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
      state.loading = false;
      state.pagesFetched = true;
    },

    // -------- FETCH SINGLE PAGE --------
    fetchPageRequest: (state, action: PayloadAction<{ pageId: string }>) => {
      state.currentPageLoading = true;
      state.currentPageError = null;
      console.log("action", action);
    },
    fetchPageSuccess: (state, action: PayloadAction<{ page: Page }>) => {
      state.currentPage = action.payload.page;
      state.currentPageLoading = false;
    },
    fetchPageFailure: (state, action: PayloadAction<ApiError>) => {
      state.currentPageError = action.payload;
      state.currentPageLoading = false;
    },

    // -------- FORM SUBMISSION --------
    submitPageRequest: (
      state,
      action: PayloadAction<{
        data: Partial<Page>;
        pageId?: string | null;
      }>,
    ) => {
      if (!action.payload.data) {
        return;
      }
      state.form.loading = true;
      state.form.error = null;
      state.form.success = false;
    },
    submitPageSuccess: (state, action: PayloadAction<{ data: Page }>) => {
      state.form.loading = false;
      state.form.success = true;
      const pageId = action.payload.data.id;
      if (!pageId) {
        return;
      }
      const existingIndex = state.items.findIndex((item) => item.id === pageId);
      if (existingIndex === -1) {
        state.items = [action.payload.data, ...state.items];
      } else {
        state.items = state.items.map((item) => {
          if (item.id === pageId) {
            return action.payload.data;
          }
          return item;
        });
      }
    },
    submitPageFailure: (state, action: PayloadAction<ApiError>) => {
      state.form.loading = false;
      state.form.error = action.payload;
      state.form.success = false;
    },
    // reset form state
    pageFormReset: (state) => {
      state.form.loading = false;
      state.form.error = null;
      state.form.success = false;
    },

    // -------- DELETE PAGE --------
    deletePageRequest: (state, action: PayloadAction<{ pageId: string }>) => {
      if (!action.payload.pageId) {
        return;
      }
      state.loading = true;
      state.error = null;
    },
    deletePageSuccess: (state, action: PayloadAction<{ pageId: string }>) => {
      state.items = state.items.filter(
        (page) => page.id !== action.payload.pageId,
      );
      state.loading = false;
    },
    deletePageFailure: (state, action: PayloadAction<ApiError>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // -------- CREATE PAGES FROM TEMPLATE --------
    createPagesFromTemplateRequest: (
      state,
      _action: PayloadAction<{ templateId: string; pageIds?: string[] }>,
    ) => {
      state.form.loading = true;
      state.form.error = null;
      state.form.success = false;
    },
    createPagesFromTemplateSuccess: (
      state,
      action: PayloadAction<{ createdPages: Page[] }>,
    ) => {
      // Add the created pages to the list
      state.items = [...state.items, ...action.payload.createdPages];
      state.form.loading = false;
      state.form.success = true;
    },
    createPagesFromTemplateFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.form.loading = false;
      state.form.error = action.payload;
      state.form.success = false;
    },

    // -------- CREATE PAGE FROM TEMPLATE --------
    createPageFromTemplateRequest: (
      state,
      _action: PayloadAction<CreatePageRequest>,
    ) => {
      state.form.loading = true;
      state.form.error = null;
      state.form.success = false;
    },
    createPageFromTemplateSuccess: (
      state,
      action: PayloadAction<{ createdPage: Page }>,
    ) => {
      // Add the created page to the list
      state.items = [...state.items, action.payload.createdPage];
      state.form.loading = false;
      state.form.success = true;
    },
    createPageFromTemplateFailure: (state, action: PayloadAction<ApiError>) => {
      state.form.loading = false;
      state.form.error = action.payload;
      state.form.success = false;
    },

    // -------- DRAFT ACTIONS --------
    // Fetch drafts
    fetchDraftsRequest: (state, action: PayloadAction<{ pageId: string }>) => {
      const { pageId: _pageId } = action.payload;
      state.draftsLoading = true;
      state.draftsError = null;
    },
    fetchDraftsSuccess: (
      state,
      action: PayloadAction<{ drafts: PageDraft[] }>,
    ) => {
      state.drafts = action.payload.drafts;
      state.draftsLoading = false;
    },
    fetchDraftsFailure: (state, action: PayloadAction<ApiError>) => {
      state.draftsError = action.payload;
      state.draftsLoading = false;
    },

    // Create draft
    createDraftRequest: (state, action: PayloadAction<{ pageId: string }>) => {
      const { pageId: _pageId } = action.payload;
      state.draftForm.loading = true;
      state.draftForm.error = null;
      state.draftForm.success = false;
    },
    createDraftSuccess: (
      state,
      action: PayloadAction<{ draftResponse: PageDraftResponse }>,
    ) => {
      state.draftForm.loading = false;
      state.draftForm.success = true;
      // Add the new draft to the drafts list
      state.drafts = [...state.drafts, action.payload.draftResponse.draft];
    },
    createDraftFailure: (state, action: PayloadAction<ApiError>) => {
      state.draftForm.loading = false;
      state.draftForm.error = action.payload;
      state.draftForm.success = false;
    },

    // Update draft
    updateDraftRequest: (
      state,
      action: PayloadAction<{ pageId: string; draftId: string }>,
    ) => {
      const { pageId: _pageId, draftId: _draftId } = action.payload;
      state.draftForm.loading = true;
      state.draftForm.error = null;
      state.draftForm.success = false;
    },
    updateDraftSuccess: (
      state,
      action: PayloadAction<{ draftResponse: PageDraftResponse }>,
    ) => {
      state.draftForm.loading = false;
      state.draftForm.success = true;
      // Update the draft in the drafts list
      const updatedDraft = action.payload.draftResponse.draft;
      state.drafts = state.drafts.map((draft) =>
        draft.id === updatedDraft.id ? updatedDraft : draft,
      );
    },
    updateDraftFailure: (state, action: PayloadAction<ApiError>) => {
      state.draftForm.loading = false;
      state.draftForm.error = action.payload;
      state.draftForm.success = false;
    },

    // Reset draft form
    draftFormReset: (state) => {
      state.draftForm.loading = false;
      state.draftForm.error = null;
      state.draftForm.success = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchPagesRequest,
  fetchPagesSuccess,
  fetchPagesFailure,
  fetchPageRequest,
  fetchPageSuccess,
  fetchPageFailure,
  submitPageRequest,
  submitPageSuccess,
  submitPageFailure,
  deletePageRequest,
  deletePageSuccess,
  deletePageFailure,
  pageFormReset,
  createPagesFromTemplateRequest,
  createPagesFromTemplateSuccess,
  createPagesFromTemplateFailure,
  createPageFromTemplateRequest,
  createPageFromTemplateSuccess,
  createPageFromTemplateFailure,
  fetchDraftsRequest,
  fetchDraftsSuccess,
  fetchDraftsFailure,
  createDraftRequest,
  createDraftSuccess,
  createDraftFailure,
  updateDraftRequest,
  updateDraftSuccess,
  updateDraftFailure,
  draftFormReset,
} = pageSlice.actions;

export default pageSlice.reducer;
